import React from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules, getFormattedFiatCurrencyNumber } from '@utils';

import { useStore } from '@modules';

import DonutChart from '@components/DonutChart';

import Captions from './Captions';
import { COLORS, INITIAL_OFFSET, MAX_PERCENTAGE, OTHER_COLOR } from './const';
import { TBalance, TCircle } from './types';

import style from './BalanceChart.scss';

const cn = bemCssModules(style, 'BalanceChart');

const getCircles = (balances: TBalance[]): TCircle[] =>
  balances
    .filter(item => item.balance > 0)
    .reduce<TCircle[]>((acc, cur, i) => {
      const dashArray = `${cur.percent} ${MAX_PERCENTAGE - cur.percent}`;
      const color = COLORS[i] || OTHER_COLOR;
      const offset = !i ? INITIAL_OFFSET : acc[i - 1].dashOffset;
      const dashOffset = cur.percent + offset;
      const circle = { ...cur, dashArray, color, dashOffset };
      return [...acc, circle];
    }, []);

export type TBalanceChart = {
  chartClass?: string;
  captionsClass?: string;
  onlyCurrencyForMobile?: boolean;
  isOverview?: boolean;
};

const BalanceChart: CustomFC<TBalanceChart> = ({
  chartClass,
  captionsClass,
  onlyCurrencyForMobile,
  isOverview,
  className,
}) => {
  const { t } = useTranslation();
  const [{ user, wallet }] = useStore();
  const userData = user.data;
  const walletData = wallet?.data;

  const fiatCurrency = userData?.fiat_currency || CONST.defaultFiatCurrency;

  const amount = walletData
    ? Object.values(walletData).reduce((acc, { fiat_balance }) => acc + +fiat_balance, 0)
    : 0;

  const total = getFormattedFiatCurrencyNumber({ amount, currency: fiatCurrency });

  const balances = walletData
    ? Object.values(walletData)
        .filter(value => +value.fiat_balance > 0)
        .map(({ fiat_currency, fiat_balance, percent, currency }) => ({
          label: currency,
          balance: +fiat_balance,
          percent: +percent * 100,
          fiatCurrency: fiat_currency,
        }))
        .sort((a, b) => b.balance - a.balance)
    : [];

  const otherBalances = balances.slice(COLORS.length).reduce<TBalance>(
    (a, c) => {
      a.balance += c.balance;
      a.percent += c.percent;

      return a;
    },
    { balance: 0, percent: 0, fiatCurrency, label: t('public.other') }
  );

  const formattedBalances = balances.length
    ? [...balances.slice(0, COLORS.length), otherBalances]
    : [];

  const circles: TCircle[] = getCircles(formattedBalances);

  return (
    <figure className={cn(null, [className])}>
      <div className={cn('content')}>
        <DonutChart
          circles={circles}
          total={total}
          className={chartClass}
          isOverview={isOverview}
        />
      </div>
      <Captions
        className={captionsClass}
        circles={circles}
        onlyCurrencyForMobile={onlyCurrencyForMobile}
      />
    </figure>
  );
};

export default BalanceChart;
